import {DataStatus} from 'platform/components';

import {isNotNil} from 'ramda';

import {useGetCustomerPaymentQuery} from '@dms/api/finance';
import {
  PaymentAllowedAction,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentByIdQuery,
  useGetCheckoutPaymentIssueRestrictionQuery,
} from '@dms/api/sales';

import {CheckoutPaymentCardV2Props} from '../types/CheckoutPaymentCardV2Props';
import {CheckoutPaymentCardContentV2} from './CheckoutPaymentCardContentV2';

const ISSUE_ACTIONS: PaymentAllowedAction[] = [
  'ISSUE_INVOICE',
  'ISSUE_FULL_CORRECTION',
  'ISSUE_PARTIAL_CORRECTION',
];

export function CheckoutPaymentCardV2(props: CheckoutPaymentCardV2Props) {
  const {
    data: paymentData,
    isLoading: isPaymentLoading,
    isError: isPaymentError,
  } = useGetCheckoutPaymentByIdQuery({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
  });

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const {
    data: customerPayment,
    isFetching: isCustomerPaymentFetching,
    isError: isCustomerPaymentError,
  } = useGetCustomerPaymentQuery({customerId: props.customerId});

  const allowedActions = paymentData?.uiCapabilities?.allowedActions ?? [];

  const canIssue = ISSUE_ACTIONS.some((action) => allowedActions.includes(action));

  const {
    data: issueRestrictions,
    isLoading: isIssueRestrictionsLoading,
    isError: isIssueRestrictionsError,
  } = useGetCheckoutPaymentIssueRestrictionQuery(
    {checkoutId: props.checkoutId, paymentId: props.paymentId},
    {
      refetchOnMountOrArgChange: true,
      skip: !canIssue,
    }
  );

  const isLoading =
    isPaymentLoading ||
    isCustomerPaymentFetching ||
    isCheckoutLoading ||
    isIssueRestrictionsLoading;
  const isError =
    isPaymentError || isCustomerPaymentError || isCheckoutError || isIssueRestrictionsError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      {isNotNil(paymentData) ? (
        <CheckoutPaymentCardContentV2
          paymentId={props.paymentId}
          checkoutId={props.checkoutId}
          customerId={props.customerId}
          defaultPaymentType={props.defaultPaymentType}
          isEditingDisabled={props.isEditingDisabled}
          isCompletingDisabled={props.isCompletingDisabled}
          serviceCaseId={props.serviceCaseId}
          serviceOrderId={props.serviceOrderId}
          onAddAnotherDepositClick={props.onAddAnotherDepositClick}
          onRemoveDepositClick={props.onRemoveDepositClick}
          isBalancePaymentIssued={props.isBalancePaymentIssued}
          contextTarget={props.contextTarget}
          contextId={props.contextId}
          actionContext={props.actionContext}
          payment={paymentData}
          checkout={checkoutData}
          customerPayment={customerPayment}
          issueRestrictions={issueRestrictions}
          data-testid={props['data-testid']}
        />
      ) : null}
    </DataStatus>
  );
}
